/**
 * Pending path after login: sessionStorage (tab-scoped), consume-once, cleared on logout.
 * Prevents stale redirects across browser sessions (unlike localStorage).
 */

export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'rosie_post_login_redirect';

/** After API 403 logout, skip re-saving current URL as post-login target (avoids redirect loop). */
const POST_LOGIN_SUPPRESS_PRESERVE_KEY =
  'rosie_post_login_suppress_preserve_next';

/**
 * Returns true if path is a safe in-app relative URL (open-redirect hardening).
 */
export function isSafeInternalPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  const lower = path.toLowerCase();
  const scriptScheme = `${['java', 'script'].join('')}:`;
  const dataScheme = `${['da', 'ta'].join('')}:`;
  if (lower.includes(scriptScheme) || lower.includes(dataScheme))
    return false;
  return true;
}

export function setPostLoginRedirectFromLocation(
  pathname: string,
  search: string,
  hash: string,
): void {
  if (!isSafeInternalPath(pathname)) return;
  const path = `${pathname}${search || ''}${hash || ''}`;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
  } catch {
    // ignore quota / private mode
  }
}

export function consumePostLoginRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    if (!raw) return null;
    const pathnameOnly = raw.split('?')[0].split('#')[0];
    if (!isSafeInternalPath(pathnameOnly)) {
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

/** Dedupe consume across React Strict Mode remounts (same location.key). */
const consumedRedirectByLocationKey = new Map<string, string | null>();

export function consumePostLoginRedirectForKey(
  locationKey: string,
): string | null {
  if (consumedRedirectByLocationKey.has(locationKey)) {
    return consumedRedirectByLocationKey.get(locationKey)!;
  }
  const next = consumePostLoginRedirect();
  consumedRedirectByLocationKey.set(locationKey, next);
  return next;
}

export function clearPostLoginRedirect(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** One-shot: next `RedirectToLoginPreservingNext` mount should not stash the current path. */
export function setSuppressNextPostLoginPreserve(): void {
  try {
    sessionStorage.setItem(POST_LOGIN_SUPPRESS_PRESERVE_KEY, '1');
  } catch {
    // ignore quota / private mode
  }
}

function readAndClearSuppressPreserveFromStorage(): boolean {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_SUPPRESS_PRESERVE_KEY);
    sessionStorage.removeItem(POST_LOGIN_SUPPRESS_PRESERVE_KEY);
    return raw === '1';
  } catch {
    return false;
  }
}

/** Dedupe across React Strict Mode remounts (same location.key). */
const consumedSuppressByLocationKey = new Map<string, boolean>();

export function consumeSuppressNextPostLoginPreserveForKey(
  locationKey: string,
): boolean {
  if (consumedSuppressByLocationKey.has(locationKey)) {
    return consumedSuppressByLocationKey.get(locationKey)!;
  }
  const next = readAndClearSuppressPreserveFromStorage();
  consumedSuppressByLocationKey.set(locationKey, next);
  return next;
}

/**
 * Absolute URL for sharing (same origin + safe path).
 */
export function getRosieAbsoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  const safePath = path.startsWith('/') ? path : `/${path}`;
  if (!isSafeInternalPath(safePath.split('?')[0].split('#')[0])) {
    return `${window.location.origin}/`;
  }
  return `${window.location.origin}${safePath}`;
}
